import {
  type ChangesetHistory,
  type ConfigurationOperation,
  isParameterDefinitionCreateOperation,
  isTemplateCreateOperation,
  isTemplateHideOperation,
  type ParameterDefinitionCreateOperation,
  type ReconciliationSpecCreateOperation,
  type RegisteredTenantCreateOperation,
} from '@festina/life-general-types/life'
import {
  type AllConciseWorkspaces,
  type BranchKey,
  LayerKey,
  OperationLayerMap,
  resolveTopologicalPath,
  resolveTopologicalPathForBlock,
  STANDARD_BRANCH,
  type Tree,
  type WorkspaceKey,
} from '@festina-life/api'
import {
  type ConfigurableServer,
  dateUtils,
  type Optional,
} from '@festina-life/core'

import { READ_ONLY_WORKSPACE } from '../system/constants'
import type { TemplateVersionInfo } from '../types/templateModel'
import { isEqualBranch } from '../uiModels/branches/helpers'
import { getLiteralName } from '../uiModels/enumLiterals/helpers'
import type {
  EnumDefinition,
  EnumLiteral,
} from '../uiModels/enumLiterals/types'
import {
  getLastCreatedEntity,
  glMapOperationFindIndex,
  isGlMapActiveAtDate,
} from '../uiModels/glMapOperations/helpers'
import type {
  GlMapOperation,
  IndexedGlMapOperations,
} from '../uiModels/glMapOperations/types'
import {
  parameterDefinitionFindFn,
  pushSorted,
  templateOperationFindFn,
  userActionsForOperation,
} from '../uiModels/helpers'
import { isParameterDefinitionActiveAtDate } from '../uiModels/parameterOperations/helpers'
import type { IndexedParameterDefinitionCreateOperations } from '../uiModels/parameterOperations/types'
import {
  isReconciliationSpecActiveAtDate,
  reconciliationSpecOperationFindFn,
} from '../uiModels/reconciliationSpecOperations/helpers'
import type { IndexedReconciliationSpecOperations } from '../uiModels/reconciliationSpecOperations/types'
import type { IndexedRegisteredTenantCreateOperations } from '../uiModels/registeredTenantCreateOperations/types'
import { getTemplateOperationFromDate } from '../uiModels/templateOperations/helpers'
import type {
  IndexedTemplateOperations,
  TemplateOperation,
} from '../uiModels/templateOperations/types'
import type {
  IndexedOperations,
  OperationWithSourceInfo,
} from '../uiModels/types'
import type {
  ConfigurationItemsByBlock,
  ConfigurationItemsByLayer,
} from './operationsContext'

export const getWorkspacesForBranch = (
  conciseWorkspaces: AllConciseWorkspaces,
  branchKey: Optional<BranchKey>,
) =>
  branchKey
    ? (conciseWorkspaces[branchKey.server] ?? []).filter(
        (w) => w.baseBranchName === branchKey.name,
      )
    : []

export const isEqualWorkspace = (
  key1: Optional<WorkspaceKey>,
  key2: Optional<WorkspaceKey>,
) => {
  if (!key1 || !key2) {
    return key1 == key2
  }

  return (
    isEqualBranch(key1.branchKey, key2.branchKey) && key1.name === key2.name
  )
}

export const workspaceKeyFromConciseWorkspaces = ({
  branch,
  conciseWorkspaces,
  workspaceName,
  server,
}: {
  workspaceName: Optional<string>
  branch: Optional<string>
  conciseWorkspaces: AllConciseWorkspaces
  server: ConfigurableServer
}) => {
  if (
    !workspaceName ||
    workspaceName === READ_ONLY_WORKSPACE ||
    !branch ||
    branch === STANDARD_BRANCH
  ) {
    return undefined
  }

  const conciseWorkspace = conciseWorkspaces[server]?.find(
    ({ name }) => name === workspaceName,
  )

  return conciseWorkspace
    ? {
        name: workspaceName,
        branchKey: { name: branch, server },
      }
    : undefined
}

const findActiveVersionInfoForDate = (
  versions: TemplateVersionInfo[],
  forDate: Date,
) =>
  versions.findLast(
    (v) =>
      !v.fromDate ||
      dateUtils.dateUtcAsLocal(v.fromDate).valueOf() <= forDate.getTime(),
  )

/**
 * Given a template model, this function will enrich it, inserting TemplateVersionInfo elements inside the parents and
 * children, based on the parentId and childrenId values, respectively.
 */
export const enrichTemplateVersionInfos = ({
  block,
  layer,
  lowerLayers,
  model,
  viewDate,
}: {
  layer: LayerKey
  block: string
  lowerLayers: LayerKey[]
  model: Map<string, TemplateVersionInfo[]>
  viewDate: Date
}) =>
  new Map(
    Array.from(model.entries()).map(([identifier, versionInfos]) => {
      const activeVersionForDate = findActiveVersionInfoForDate(
        versionInfos,
        viewDate,
      )

      const mappedVersionInfos = versionInfos.map((versionInfo) => {
        if (versionInfo.parentId) {
          const parentCandidates = (
            model.get(versionInfo.parentId) ?? []
          ).filter(
            (candidateVersion) =>
              candidateVersion.layer.equals(layer) ||
              lowerLayers.some((lowerLayer) =>
                lowerLayer.equals(candidateVersion.layer),
              ),
          )

          versionInfo.parent =
            findActiveVersionInfoForDate(parentCandidates, viewDate) ??
            generateVersionInfo({
              identifier: versionInfo.parentId,
              isDraft: false,
              layer,
              block,
              templateOperation: undefined,
              children: [],
              changesetHistory: undefined,
            })
        }

        if (activeVersionForDate === versionInfo) {
          if (!versionInfo.parent?.children.includes(versionInfo)) {
            versionInfo.parent?.children.push(versionInfo)
          }

          if (
            !versionInfo.parent?.childrenIds.includes(versionInfo.identifier)
          ) {
            versionInfo.parent?.childrenIds.push(versionInfo.identifier)
          }
        }

        if (versionInfo.replacesId) {
          const replacesVersionCandidates = (
            model.get(versionInfo.replacesId) ?? []
          ).filter(
            (candidateVersion) =>
              candidateVersion.layer.equals(layer) ||
              lowerLayers.some((lowerLayer) =>
                lowerLayer.equals(candidateVersion.layer),
              ),
          )

          versionInfo.replaces =
            findActiveVersionInfoForDate(replacesVersionCandidates, viewDate) ??
            generateVersionInfo({
              identifier: versionInfo.replacesId,
              isDraft: false,
              layer,
              block,
              templateOperation: undefined,
              replacedBy: [versionInfo],
              changesetHistory: undefined,
            })

          // We only update replaced by if the current version info is active on the current view date. If its not the replaced version might be replaced by a different version.
          if (activeVersionForDate === versionInfo) {
            if (
              !versionInfo.replaces.replacedByIds?.includes(
                versionInfo.identifier,
              )
            ) {
              versionInfo.replaces.replacedByIds = [
                ...(versionInfo.replaces.replacedByIds ?? []),
                versionInfo.identifier,
              ]
            }
            versionInfo.replaces.replacedBy = [
              ...(versionInfo.replaces.replacedBy ?? []),
              versionInfo,
            ]
          }
        }

        return versionInfo
      })

      return [identifier, mappedVersionInfos]
    }),
  )

export const generateModel = (
  templateOperations: Map<string, IndexedTemplateOperations>,
): Map<string, TemplateVersionInfo[]> =>
  new Map(
    Array.from(templateOperations.entries()).map(
      ([templateIdFromPath, indexCreateOperation]) =>
        [
          templateIdFromPath,
          indexCreateOperation.operations.map(
            ({ isDraft, block, layer, internalOperation, changesetHistory }) =>
              generateVersionInfo({
                identifier: indexCreateOperation.templateId,
                templateOperation: internalOperation,
                isDraft,
                layer,
                block,
                changesetHistory,
              }),
          ),
        ] as const,
    ),
  )

export const generateVersionInfo = ({
  identifier,
  templateOperation,
  isDraft,
  block,
  layer,
  children,
  replacedBy,
  changesetHistory,
}: {
  identifier: string
  templateOperation: Optional<TemplateOperation>
  isDraft: boolean
  block: string
  layer: LayerKey
  children?: TemplateVersionInfo[]
  replacedBy?: Optional<TemplateVersionInfo[]>
  changesetHistory: Optional<ChangesetHistory>
}): TemplateVersionInfo => ({
  operationKey: templateOperation?.operationKey,
  parentId: isTemplateCreateOperation(templateOperation)
    ? templateOperation?.entity?.parent
    : undefined,
  parent: null,
  children: children ?? [],
  childrenIds: children?.map((c) => c.identifier) ?? [],
  fromDate: templateOperation
    ? getTemplateOperationFromDate(templateOperation)
    : undefined,
  identifier,
  block,
  layer,
  isDraft,
  package: getTemplateOperationPackage(templateOperation),
  replacesId:
    templateOperation && isTemplateCreateOperation(templateOperation)
      ? templateOperation?.entity?.replaces
      : undefined,
  replaces: null,
  replacedBy,
  replacedByIds: Array.from(
    new Set(replacedBy?.map((version) => version.identifier).values()),
  ),
  isMissing: !templateOperation,
  changesetHistory,
  isHideVersion:
    !!templateOperation && isTemplateHideOperation(templateOperation),
  actions: templateOperation
    ? userActionsForOperation(templateOperation, changesetHistory)
    : [],
})

function getTemplateOperationPackage(
  templateOperation: Optional<TemplateOperation>,
): Optional<string[]> {
  if (!templateOperation) {
    return null
  }

  if (isTemplateCreateOperation(templateOperation)) {
    return templateOperation.entity?.packageName?.split('.') ?? []
  }

  return templateOperation.packageName?.split('.') ?? []
}

export const getOperationOnLayer = <V, T extends ConfigurationItemsByLayer<V>>({
  operations,
  layer,
  layerTopology,
  operationId,
  isActiveAtViewDate,
}: {
  operations: T
  layer: LayerKey
  layerTopology: Optional<Tree>
  operationId: string
  isActiveAtViewDate: (value: Optional<V>) => boolean
}): Optional<V> => {
  const aggregatedOperations = getAggregatedLayerOperations({
    operations,
    layer,
    layerTopology,
    isActiveAtViewDate,
  })

  return aggregatedOperations.get(operationId)
}

export const getAggregatedLayerOperations = <
  V,
  T extends ConfigurationItemsByLayer<V>,
>({
  operations,
  layer,
  layerTopology,
  isActiveAtViewDate,
  combineNonAnnulledLayerOperations,
}: {
  operations: T
  layer: LayerKey
  layerTopology: Optional<Tree>
  isActiveAtViewDate: (value: Optional<V>) => boolean
  combineNonAnnulledLayerOperations?: (params: {
    childLayerOperation: V
    parentLayerOperation: V
  }) => Optional<V>
}): Map<string, V> => {
  const topologicalPath = resolveTopologicalPath(layer, layerTopology)

  const topologicalPathWithTenant =
    layer.layerId && layer.tenantId
      ? [
          ...topologicalPath,
          new LayerKey({ layerId: layer.layerId, tenantId: layer.tenantId }),
        ]
      : topologicalPath

  const inverseTopologicalPath = topologicalPathWithTenant.toReversed()

  const aggregatedOperations = new Map<string, V>()

  for (const layerKey of inverseTopologicalPath) {
    const operationsOnLayer = operations.get(layerKey)
    Array.from(operationsOnLayer?.entries() ?? []).forEach(
      ([operationId, value]) => {
        if (isActiveAtViewDate(value)) {
          const aggregatedOperationForId = aggregatedOperations.get(operationId)
          // The first operation we find traversing from child layers to parent layers is the one that prevails.
          // We only combine operations if the user has provided a function to do so.
          if (!aggregatedOperationForId) {
            aggregatedOperations.set(operationId, value)
          } else if (combineNonAnnulledLayerOperations) {
            const combinedOperation = combineNonAnnulledLayerOperations({
              childLayerOperation: aggregatedOperationForId,
              parentLayerOperation: value,
            })

            if (combinedOperation) {
              aggregatedOperations.set(operationId, combinedOperation)
            }
          }
        }
      },
    )
  }

  return aggregatedOperations
}

export const getApplicationViewOperations = <V>({
  operations,
  layer,
  layerTopology,
  block,
  blockTopology,
  isActiveAtViewDate,
  combineBlockOperations,
  combineNonAnnulledLayerOperations,
}: {
  operations: ConfigurationItemsByBlock<V>
  layer: LayerKey
  block: string
  blockTopology: Tree
  layerTopology: Optional<Tree>
  isActiveAtViewDate: (value: Optional<V>) => boolean
  combineBlockOperations: (params: {
    baseOperation: Optional<V>
    newOperation: Optional<V>
    topologicalBlockPath: string[]
  }) => Optional<V>
  // Combines layer operations from child to parent layers, making sure that child operations prevail
  combineNonAnnulledLayerOperations?: (params: {
    childLayerOperation: V
    parentLayerOperation: V
  }) => Optional<V>
}): Map<string, V> => {
  const topologicalBlockPath = resolveTopologicalPathForBlock(
    block,
    blockTopology,
  )

  // We initially aggregate the blocks into a single map of layer and thereafter we aggregate the layers into a single map of operation id and operation.
  // This ensures that config items defined on high layers within high blocks, are able to override config items defined in lower blocks within lower layers.
  const aggregatedLayers = topologicalBlockPath.reduce((acc, currentBlock) => {
    const layeredOperationsForBlock =
      operations.get(currentBlock) ?? new OperationLayerMap<Map<string, V>>()

    Array.from(layeredOperationsForBlock.entries()).forEach(
      ([layer, operations]) => {
        const accumulatedLayerOperations =
          acc.get(layer) ?? new Map<string, V>()
        acc.set(layer, accumulatedLayerOperations)

        Array.from(operations.entries()).forEach(([operationId, operation]) => {
          const currentOperation = accumulatedLayerOperations.get(operationId)
          const combinedOperation = combineBlockOperations({
            baseOperation: currentOperation,
            newOperation: operation,
            topologicalBlockPath,
          })

          if (combinedOperation) {
            accumulatedLayerOperations.set(operationId, combinedOperation)
          }
        })
      },
    )

    return acc
  }, new OperationLayerMap<Map<string, V>>())

  return getAggregatedLayerOperations({
    operations: aggregatedLayers,
    layer,
    layerTopology,
    isActiveAtViewDate,
    combineNonAnnulledLayerOperations,
  })
}

/**
 * View date to use when a configuration item must be resolved regardless of when its
 * versions start applying. The application view omits items whose first operation lies
 * in the future, which would hide them from validations such as duplicate detection.
 * Passing this date as `viewAt` keeps the block and layer aggregation while disabling
 * the date filtering, since no operation can start after the date inputs allow.
 */
export const ALL_VERSIONS_VIEW_DATE = dateUtils.MAX_DATE

/**
 * The layer a configuration item is already defined on, when that is not the layer the
 * draft targets. Configuration items must always be created on the layer they are
 * defined on, as the versions of a child layer shadow the ones inherited from its
 * ancestor layers, so a version added further down would silently drop the existing
 * ones. All the versions of a resolved item come from a single layer, which is why the
 * first one identifies the definition.
 */
export const getShadowedDefinitionLayer = <T extends ConfigurationOperation>({
  indexedOperations,
  layer,
}: {
  indexedOperations: Optional<IndexedOperations<T>>
  layer: LayerKey
}): Optional<LayerKey> => {
  const definitionLayer = indexedOperations?.operations.at(0)?.layer

  if (!definitionLayer) {
    return undefined
  }

  const definitionLayerKey = new LayerKey(definitionLayer)

  return definitionLayerKey.equals(layer) ? undefined : definitionLayerKey
}

export const isTemplateActiveForAggregatedOperations =
  (viewAt: Date) => (value: Optional<IndexedTemplateOperations>) =>
    !!value?.operations.some((operation) => {
      const operationFromDate = getTemplateOperationFromDate(
        operation.internalOperation,
      )
      const operationDateTime = operationFromDate
        ? dateUtils.dateUtcAsLocal(operationFromDate).valueOf()
        : Number.NEGATIVE_INFINITY

      return operationDateTime <= viewAt.getTime()
    })

export const getApplicationViewConfiguredEnums = ({
  configuredEnums,
  block,
  layer,
  layerTopology,
  blockTopology,
}: {
  configuredEnums: ConfigurationItemsByBlock<EnumDefinition>
  block: string
  layer: LayerKey
  layerTopology: Optional<Tree>
  blockTopology: Tree
}): Map<string, EnumDefinition> =>
  getApplicationViewOperations({
    operations: configuredEnums,
    block,
    layer,
    layerTopology,
    blockTopology,
    isActiveAtViewDate: (_value: Optional<EnumDefinition>) => true,
    combineNonAnnulledLayerOperations: ({
      childLayerOperation,
      parentLayerOperation,
    }) => {
      const doesChildOperationHaveDefinition =
        !!childLayerOperation.internalOperation
      const nextDefinitionBase = doesChildOperationHaveDefinition
        ? childLayerOperation
        : parentLayerOperation

      if (!nextDefinitionBase) {
        return undefined
      }

      const addedLiterals = doesChildOperationHaveDefinition // This is the inverse of the above. If we're taking the new definition we need to add to it the literals from the base operation, and vice versa.
        ? parentLayerOperation.literals
        : childLayerOperation.literals

      const combinedLiterals = combineLiterals({
        base: nextDefinitionBase?.literals ?? [],
        next: addedLiterals ?? [],
      })

      return {
        ...nextDefinitionBase,
        literals: combinedLiterals,
        annulOperations: [],
      }
    },
    combineBlockOperations: ({
      baseOperation: evaluatedBaseOperation,
      newOperation: evaluatedNewOperation,
    }) => {
      const baseOperation = evaluatedBaseOperation?.annulOperations.length
        ? undefined
        : evaluatedBaseOperation
      const newOperation = evaluatedNewOperation?.annulOperations.length
        ? undefined
        : evaluatedNewOperation
      const doesNewOperationHaveDefinition = !!newOperation?.internalOperation
      const combineFromDefinition = baseOperation ?? newOperation

      const nextEnumDefinition = combineFromDefinition
        ? {
            ...combineFromDefinition,

            // Literals have to be properly combined using the combineLiterals function to ensure that literals defined in different operations are merged correctly.
            // They're initialised to the base literals.
            literals: (baseOperation?.literals ?? [])
              .filter(
                (literal) =>
                  literal.type === 'modeled' || !literal.annulOperations.length,
              )
              .map((literal) => ({
                ...structuredClone(literal),
                ...(literal.type === 'configured'
                  ? { layer: new LayerKey(literal.layer) }
                  : {}),
              })),
            annulOperations: [],
          }
        : undefined

      if (nextEnumDefinition) {
        if (doesNewOperationHaveDefinition) {
          nextEnumDefinition.layer = newOperation.layer
          nextEnumDefinition.block = newOperation.block
          nextEnumDefinition.actions = newOperation.actions
          nextEnumDefinition.changesetHistory = newOperation.changesetHistory
          nextEnumDefinition.internalOperation = newOperation.internalOperation
        }

        nextEnumDefinition.literals = combineLiterals({
          base: nextEnumDefinition.literals,
          next:
            newOperation?.literals.filter(
              (literal) =>
                literal.type === 'modeled' || !literal.annulOperations.length,
            ) ?? [],
        })

        nextEnumDefinition.isDraft =
          (combineFromDefinition?.isDraft &&
            nextEnumDefinition.block === block) ||
          nextEnumDefinition.literals.some(
            (literal) =>
              literal.type === 'configured' &&
              literal.isDraft &&
              literal.block === block,
          )
      }

      return nextEnumDefinition
    },
  })

const nonAnnulledVersions = <T extends ConfigurationOperation>(
  indexedVersions: Optional<IndexedOperations<T>>,
) =>
  (indexedVersions?.operations ?? []).filter(
    (operation) => !operation.annulOperations.length,
  )

/**
 * Merges the versions of a lower block into the ones already combined for a higher
 * block, leaving annulled versions out and keeping the result sorted. An item whose
 * versions are all annulled no longer applies and is dropped altogether.
 */
const combineIndexedBlockVersions =
  <T extends ConfigurationOperation, V extends IndexedOperations<T>>({
    block,
    generateFindFn,
    onVersionsCombined,
  }: {
    block: string
    generateFindFn: (params: {
      operation: OperationWithSourceInfo<T>
      topologicalBlockPath: string[]
    }) => (
      forOperation: OperationWithSourceInfo<T>,
    ) => (compareTo: OperationWithSourceInfo<T>) => boolean
    /**
     * Refreshes the fields derived from the combined versions, such as the entity of
     * the last create operation.
     */
    onVersionsCombined: (combinedVersions: V) => void
  }) =>
  ({
    baseOperation,
    newOperation,
    topologicalBlockPath,
  }: {
    baseOperation: Optional<V>
    newOperation: Optional<V>
    topologicalBlockPath: string[]
  }): Optional<V> => {
    const baseVersions = nonAnnulledVersions(baseOperation)
    const newVersions = nonAnnulledVersions(newOperation)
    const combineFrom =
      (baseVersions.length ? baseOperation : undefined) ??
      (newVersions.length ? newOperation : undefined)

    if (!combineFrom) {
      return undefined
    }

    const combinedVersions: V = {
      ...combineFrom,
      operations: baseVersions.map((operation) => ({
        ...structuredClone(operation),
        layer: new LayerKey(operation.layer), // Structured clone cannot be applied to the full data structure due to the existence of LayerKey instances
      })),
    }

    newVersions.forEach((operation) =>
      pushSorted({
        array: combinedVersions.operations,
        element: operation,
        findIndexFn: generateFindFn({ operation, topologicalBlockPath }),
      }),
    )

    // Only drafts of the block in view are relevant, drafts of ancestor blocks are
    // not being edited from here.
    combinedVersions.isDraft = combinedVersions.operations.some(
      (operation) => operation.isDraft && operation.block === block,
    )

    onVersionsCombined(combinedVersions)

    return combinedVersions
  }

export const getApplicationViewTemplates = ({
  templateOperations,
  layer,
  block,
  layerTopology,
  blockTopology,
  viewAt,
}: {
  templateOperations: ConfigurationItemsByBlock<IndexedTemplateOperations>
  layer: LayerKey
  block: string
  layerTopology: Optional<Tree>
  blockTopology: Tree
  viewAt: Date
}): Map<string, IndexedTemplateOperations> =>
  getApplicationViewOperations({
    operations: templateOperations,
    layer,
    block,
    layerTopology,
    blockTopology,
    isActiveAtViewDate: isTemplateActiveForAggregatedOperations(viewAt),
    combineBlockOperations: combineIndexedBlockVersions<
      TemplateOperation,
      IndexedTemplateOperations
    >({
      block,
      generateFindFn: ({ topologicalBlockPath }) =>
        templateOperationFindFn(topologicalBlockPath),
      onVersionsCombined: (combinedVersions) => {
        combinedVersions.lastEntity = combinedVersions.operations
          .map((operation) => operation.internalOperation)
          .findLast(isTemplateCreateOperation)?.entity
      },
    }),
  })

export const getApplicationViewParameterDefinitions = ({
  parameterDefinitionCreateOperations,
  layer,
  block,
  layerTopology,
  blockTopology,
  viewAt,
}: {
  parameterDefinitionCreateOperations: ConfigurationItemsByBlock<IndexedParameterDefinitionCreateOperations>
  layer: LayerKey
  block: string
  layerTopology: Optional<Tree>
  blockTopology: Tree
  viewAt: Date
}): Map<string, IndexedParameterDefinitionCreateOperations> =>
  getApplicationViewOperations<IndexedParameterDefinitionCreateOperations>({
    operations: parameterDefinitionCreateOperations,
    layer,
    block,
    layerTopology,
    blockTopology,
    isActiveAtViewDate: isParameterDefinitionActiveAtDate(viewAt),
    combineBlockOperations: combineIndexedBlockVersions<
      ParameterDefinitionCreateOperation,
      IndexedParameterDefinitionCreateOperations
    >({
      block,
      generateFindFn: ({ topologicalBlockPath }) =>
        parameterDefinitionFindFn(topologicalBlockPath),
      onVersionsCombined: (combinedVersions) => {
        const lastCreateOperation = combinedVersions.operations
          .map((operation) => operation.internalOperation)
          .findLast(isParameterDefinitionCreateOperation)

        combinedVersions.lastParameterEntity = lastCreateOperation?.entity
        combinedVersions.packageName = lastCreateOperation?.entity?.packageName
      },
    }),
  })

/**
 * GL-maps aggregated for the block and layer in view. Versions defined on ancestor
 * blocks are merged into a single sorted list per mapping id, and a GL-map defined
 * on a child layer overrides the one inherited from its parent layers, mirroring
 * how templates and parameter definitions resolve.
 */
export const getApplicationViewGlMaps = ({
  glMapOperations,
  layer,
  block,
  layerTopology,
  blockTopology,
  viewAt,
}: {
  glMapOperations: ConfigurationItemsByBlock<IndexedGlMapOperations>
  layer: LayerKey
  block: string
  layerTopology: Optional<Tree>
  blockTopology: Tree
  viewAt: Date
}): Map<string, IndexedGlMapOperations> =>
  getApplicationViewOperations<IndexedGlMapOperations>({
    operations: glMapOperations,
    layer,
    block,
    layerTopology,
    blockTopology,
    isActiveAtViewDate: isGlMapActiveAtDate(viewAt),
    combineBlockOperations: combineIndexedBlockVersions<
      GlMapOperation,
      IndexedGlMapOperations
    >({
      block,
      generateFindFn: ({ topologicalBlockPath }) =>
        glMapOperationFindIndex(topologicalBlockPath),
      onVersionsCombined: (combinedVersions) => {
        combinedVersions.lastEntity = getLastCreatedEntity(
          combinedVersions.operations,
        )
      },
    }),
  })

/**
 * Reconciliation specs aggregated for the block and layer in view. Versions defined
 * on ancestor blocks are merged into a single sorted list per spec id, and a spec
 * defined on a child layer overrides the one inherited from its parent layers,
 * mirroring how GL maps resolve.
 */
export const getApplicationViewReconciliationSpecs = ({
  reconciliationSpecCreateOperations,
  layer,
  block,
  layerTopology,
  blockTopology,
  viewAt,
}: {
  reconciliationSpecCreateOperations: ConfigurationItemsByBlock<IndexedReconciliationSpecOperations>
  layer: LayerKey
  block: string
  layerTopology: Optional<Tree>
  blockTopology: Tree
  viewAt: Date
}): Map<string, IndexedReconciliationSpecOperations> =>
  getApplicationViewOperations<IndexedReconciliationSpecOperations>({
    operations: reconciliationSpecCreateOperations,
    layer,
    block,
    layerTopology,
    blockTopology,
    isActiveAtViewDate: isReconciliationSpecActiveAtDate(viewAt),
    combineBlockOperations: combineIndexedBlockVersions<
      ReconciliationSpecCreateOperation,
      IndexedReconciliationSpecOperations
    >({
      block,
      generateFindFn: ({ topologicalBlockPath }) =>
        reconciliationSpecOperationFindFn(topologicalBlockPath),
      onVersionsCombined: (combinedVersions) => {
        combinedVersions.lastEntity =
          combinedVersions.operations.at(
            -1,
          )?.internalOperation.reconciliationSpec
      },
    }),
  })

export const getApplicationViewRegisteredTenants = ({
  block,
  blockTopology,
  registeredTenantCreateOperations,
}: {
  block: string
  blockTopology: Tree
  registeredTenantCreateOperations: ConfigurationItemsByBlock<IndexedRegisteredTenantCreateOperations>
}) => {
  const blockTopologyPath = resolveTopologicalPathForBlock(block, blockTopology)

  const tenantsMap = blockTopologyPath.reduce((allTenants, currentBlock) => {
    const currentBlockTenants =
      registeredTenantCreateOperations.get(currentBlock)

    if (currentBlockTenants) {
      Array.from(currentBlockTenants.values()).forEach((layerTenants) => {
        Array.from(layerTenants.values()).forEach((tenantOperations) => {
          const tenantOperation = tenantOperations.operations
            .toReversed()
            .find((operation) => !operation.annulOperations.length)

          if (tenantOperation?.internalOperation.registeredTenant?.tenantId) {
            allTenants.set(
              tenantOperation.internalOperation.registeredTenant.tenantId,
              {
                ...tenantOperation,
                isDraft:
                  tenantOperation.isDraft && tenantOperation.block === block,
                layer: new LayerKey(tenantOperation.layer),
              },
            )
          }
        })
      })
    }

    return allTenants
  }, new Map<string, OperationWithSourceInfo<RegisteredTenantCreateOperation>>())

  return Array.from(tenantsMap.values())
}

const combineLiterals = ({
  base,
  next,
}: {
  base: EnumLiteral[]
  next: EnumLiteral[]
}) => {
  const combinedLiterals: EnumLiteral[] = [...base]
  next.forEach((newLiteral) => {
    const literalName = getLiteralName(newLiteral)

    const positionOfExistingLiteral = combinedLiterals.findIndex(
      (literal) => getLiteralName(literal) === literalName,
    )

    const insertInPosition =
      positionOfExistingLiteral === -1
        ? combinedLiterals.length
        : positionOfExistingLiteral

    combinedLiterals.splice(
      insertInPosition,
      positionOfExistingLiteral !== -1 ? 1 : 0,
      newLiteral,
    )
  })

  return combinedLiterals
}

export const generateUniqueWorkspaceName = (
  baseName: string,
  existingNames: string[],
): string => {
  const baseWorkspaceName = baseName ?? 'workspace'
  let candidate = `${baseWorkspaceName}-auto`
  let suffix = 2
  while (existingNames.includes(candidate)) {
    candidate = `${baseWorkspaceName}-auto-${suffix++}`
  }
  return candidate
}
